import { useEffect, useState } from "react";
import { TextField, Button, Snackbar, Alert, MenuItem } from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getNextCustomerCode } from "../services/customerService";
import { useRef } from "react";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import {
    getCustomers,
    addCustomer,
    updateCustomer,
    deleteCustomer
} from "../services/customerService";
import { formatCustomerLabel } from "../utils/formatters";
import ActionButtons from "../components/common/ActionButtons";
import { getCurrentUser } from "../utils/currentUser";

function Customer() {
    const custNameRef = useRef(null);
    const addressRef = useRef(null);
    const phoneRef = useRef(null);
    const emailRef = useRef(null);
    const gstRef = useRef(null);
    const typeRef = useRef(null);
    const statusRef = useRef(null);

    const [customers, setCustomers] = useState([]);
    const [search, setSearch] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(0);   // ✅ CHANGE THIS
    const [showInactive, setShowInactive] = useState(false);

    const [sortField, setSortField] = useState("");
    const [sortDirection, setSortDirection] = useState("asc");


    // 🔥 FILTER
    let filtered = customers.filter(c => {
        if (!showInactive && c.status === "Inactive") return false;

        const searchText = search.trim().toLowerCase();

        return (
            (c.custCode || "").toLowerCase().includes(searchText) ||
            (c.organizationName || "").toLowerCase().includes(searchText) ||
            (c.custName || "").toLowerCase().includes(searchText) ||
            (c.mobileNo || "").toString().toLowerCase().includes(searchText)
        );
    });

    // 🔥 SORT
    if (sortField) {
        filtered.sort((a, b) => {
            let valA = a[sortField] || "";
            let valB = b[sortField] || "";

            valA = valA.toString().toLowerCase();
            valB = valB.toString().toLowerCase();

            if (valA < valB) return sortDirection === "asc" ? -1 : 1;
            if (valA > valB) return sortDirection === "asc" ? 1 : -1;
            return 0;
        });
    }

    // 🔥 PAGINATION
    const paginated = filtered.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // 🔥 TOTAL PAGES (for UI)
    const totalPages = Math.ceil(filtered.length / rowsPerPage);

    useEffect(() => {
        setPage(0);
    }, [search, rowsPerPage, showInactive]);

    const [form, setForm] = useState({
        id: 0,
        custCode: "",
        organizationName: "",
        custName: "",
        address: "",
        mobileNo: "",
        emailId: "",
        gstNumber: "",
        customerType: "Banking",
        status: "Active"
    });

    const [isEdit, setIsEdit] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success"
    });

    const loadData = async () => {
        const res = await getCustomers();
        setCustomers(res.data); // ✅ store full data ONLY
    };

    // ✅ ADD THIS HERE (payload builder)
    const buildCustomerPayload = (c, status) => ({
        id: c.id,
        custCode: c.custCode,
        organizationName: c.organizationName,
        custName: c.custName,
        address: c.address,
        mobileNo: c.mobileNo,
        emailId: c.emailId,
        gstNumber: c.gstNumber,
        customerType: c.customerType,
        status
    });

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const user = getCurrentUser();

    const role =
        user?.role?.toUpperCase() || "";

    const canEdit =
        role === "ADMIN" ||
        role === "MANAGER";

    const canDelete =
        role === "ADMIN";

    //const user = JSON.parse(localStorage.getItem("user"));

    useEffect(() => {
        loadData();

        const loadCode = async () => {
            try {
                const res = await getNextCustomerCode();

                console.log("CODE FROM API:", res.data); // 👈 ADD THIS

                setForm(prev => ({
                    ...prev,
                    custCode: res.data
                }));
            } catch (err) {
                console.error("Code load failed:", err);
            }
        };

        loadCode();

    }, [showInactive]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = () => {

        if (!form.custName) {
            setSnackbar({
                open: true,
                message: "Name required",
                severity: "error"
            });
            return;
        }

        const payload = {
            id: form.id,
            custName: form.custName,
            organizationName: form.organizationName,
            address: form.address,
            mobileNo: form.mobileNo,
            emailId: form.emailId,
            gstNumber: form.gstNumber,
            customerType: form.customerType,
            status: form.status
        };

        const successHandler = () => {
            setSnackbar({
                open: true,
                message: isEdit ? "Updated successfully" : "Saved successfully",
                severity: "success"
            });

            loadData();
            resetForm();
        };

        const errorHandler = (err) => {
            console.error("Save failed:", err);

            const data = err?.response?.data;

            let message = "Save failed";

            if (typeof data === "string") {
                message = data;
            }
            else if (data?.errors) {
                const firstKey = Object.keys(data.errors)[0];
                message = data.errors[firstKey][0];
            }
            else if (data?.inner) {
                message = data.inner;
            }
            else if (data?.message) {
                message = data.message;
            }

            setSnackbar({
                open: true,
                message,
                severity: "error"
            });
        };

        if (isEdit) {
            if (!form.id || form.id === 0) {
                setSnackbar({
                    open: true,
                    message: "Invalid customer ID",
                    severity: "error"
                });
                return;
            }

            updateCustomer(form.id, payload)
                .then(successHandler)
                .catch(errorHandler);
        } else {
            addCustomer(payload)
                .then(successHandler)
                .catch(errorHandler);
        }
    };

    const resetForm = () => {
        getNextCustomerCode()
            .then(res => {
                setForm({
                    id: 0,
                    custCode: res.data,
                    organizationName: "",
                    custName: "",
                    address: "",
                    mobileNo: "",
                    emailId: "",
                    gstNumber: "",
                    customerType: "Banking",
                    status: "Active"
                });
            })
            .catch(() => {
                // ✅ fallback if API fails
                setForm({
                    id: 0,
                    custCode: "",
                    organizationName: "",
                    custName: "",
                    address: "",
                    mobileNo: "",
                    emailId: "",
                    gstNumber: "",
                    customerType: "Banking",
                    status: "Active"
                });
            });

        setIsEdit(false);
    };

    const handleEdit = (c) => {
        setForm({
            ...c,
            id: c.id   // 🔥 force id
        });
        setIsEdit(true);
    };

    const handleDelete = async (c) => {
        if (!c) return;

        if (c.status === "Inactive") {
            setSnackbar({
                open: true,
                message: "Customer already inactive",
                severity: "warning"
            });
            return;
        }

        const confirmDelete = window.confirm("Deactivate this customer?");
        if (!confirmDelete) return;

        try {
            const updated = buildCustomerPayload(c, "Inactive");

            await updateCustomer(c.id, updated);

            const label = formatCustomerLabel(c);

            setSnackbar({
                open: true,
                message: "Customer marked as Inactive",
                severity: "info"
            });

            loadData();

        } catch (err) {
            console.error("Delete failed:", err);
        }
    };


    const handlePermanentDelete = async (c) => {
        const confirmDelete = window.confirm(
            `Permanently delete "${c.organizationName}"? This cannot be undone!`
        );

        if (!confirmDelete) return;

        try {
            await deleteCustomer(c.id);

            const label = formatCustomerLabel(c);

            setSnackbar({
                open: true,
                message: "Customer permanently deleted",
                severity: "error"
            });

            loadData();

        } catch (err) {
            console.error("Permanent delete failed:", err);
        }
    };

    const handleRestore = async (c) => {
        if (!c) return;                    // ✅ safety (null check)
        if (c.status === "Active") return; // ✅ avoid unnecessary API call

        try {
            const updated = buildCustomerPayload(c, "Active");

            await updateCustomer(c.id, updated);

            const label = formatCustomerLabel(c);

            setSnackbar({
                open: true,
                message: "Customer restored",
                severity: "success"
            });

            loadData();

        } catch (err) {
            console.error("Restore failed:", err);
        }
    };

    const exportToExcel = () => {

        // ✅ EMPTY CHECK (important)
        if (!filtered || filtered.length === 0) {
            setSnackbar({
                open: true,
                message: "No data to export",
                severity: "warning"
            });
            return;
        }

        const ws = XLSX.utils.json_to_sheet(filtered);
        const wb = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(wb, ws, "Customers");

        const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

        saveAs(new Blob([buffer]), "Customers.xlsx");

    };

    const setError = (msg) => {
        setSnackbar({ open: true, message: msg, severity: "error" });
    };

      return (
        <div style={styles.container}>

            <div style={styles.header}>
                <h2>Customer Master</h2>
            </div>

            {/* FORM */}
            <div style={styles.card}>
                <div style={styles.formContainer}>

                    <div style={{ ...styles.row, alignItems: "stretch" }}>

                        {/* LEFT COLUMN */}
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>

                            <TextField
                                label="Customer Code"
                                name="custCode"
                                value={form.custCode}
                                size="small"
                                fullWidth
                                sx={styles.inputStyle}
                                InputProps={{
                                    readOnly: true   // 🔥 important
                                }}
                            />

                            <TextField
                                label="Customer Name"
                                name="custName"
                                value={form.custName}
                                onChange={handleChange}
                                size="small"
                                sx={styles.inputStyle}
                                fullWidth
                                inputRef={custNameRef}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === "Tab") {
                                        e.preventDefault();
                                        addressRef.current?.focus();
                                    }
                                }}
                            />

                            <TextField
                                label="Phone Number"
                                name="mobileNo"
                                value={form.mobileNo}
                                onChange={handleChange}
                                size="small"
                                fullWidth
                                sx={styles.inputStyle}
                                inputRef={phoneRef}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === "Tab") {
                                        e.preventDefault();
                                        emailRef.current?.focus();
                                    }
                                }}
                            />

                            <TextField
                                label="Email"
                                name="emailId"
                                value={form.emailId}
                                onChange={handleChange}
                                size="small"
                                fullWidth
                                sx={styles.inputStyle}
                                inputRef={emailRef}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === "Tab") {
                                        e.preventDefault();
                                        gstRef.current?.focus();
                                    }
                                }}
                            />

                            <TextField
                                label="GST"
                                name="gstNumber"
                                value={form.gstNumber}
                                onChange={handleChange}
                                size="small"
                                sx={styles.inputStyle}
                                fullWidth
                                inputRef={gstRef}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        typeRef.current?.focus();   // 🔥 GST → Customer Type
                                    }

                                    if (e.key === "Tab") {
                                        e.preventDefault();
                                        typeRef.current?.focus();
                                    }
                                }}
                            />
                        </div>

                        {/* RIGHT COLUMN */}
                        <div style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            minHeight: "188px"
                        }}>
                            <TextField label="Organization Name"
                                name="organizationName"
                                value={form.organizationName}
                                onChange={handleChange}
                                size="small" fullWidth
                                sx={styles.inputStyle}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === "Tab") {
                                        e.preventDefault();
                                        custNameRef.current?.focus();
                                    }
                                }}
                            />

                            <div style={{ flex: 1, display: "flex" }}>
                                  <TextField
                                      label="Address"
                                      inputRef={addressRef}
                                      name="address"
                                      value={form.address}
                                      onChange={handleChange}
                                      fullWidth
                                      multiline
                                      onKeyDown={(e) => {
                                          if (e.key === "Tab") {
                                              e.preventDefault();
                                              phoneRef.current?.focus();
                                          }
                                      }}
                                      sx={{
                                          ...styles.inputStyle,
                                          flex: 1,
                                          "& .MuiInputBase-root": {
                                              height: "100%",
                                              alignItems: "flex-start"
                                          }
                                      }}
                                  />
                            </div>
                        </div>

                    </div>

                    {/* BOTTOM ROW */}
                    <div style={styles.row}>
                        <TextField select label="Customer Type"
                            name="customerType"
                            value={form.customerType}
                            onChange={handleChange}
                            fullWidth size="small"
                            inputRef={typeRef}
                            sx={{
                                "& .MuiInputBase-input": {
                                    fontFamily: "Verdana",
                                    fontSize: "12px"
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === "Tab") {
                                    e.preventDefault();
                                    statusRef.current?.focus();
                                }
                            }}
                          >
                              <MenuItem value="Banking" sx={{ fontFamily: "Verdana", fontSize: "12px" }}>
                                  Banking
                              </MenuItem>

                              <MenuItem value="Non Banking" sx={{ fontFamily: "Verdana", fontSize: "12px" }}>
                                  Non Banking
                              </MenuItem>

                              <MenuItem value="Institutional" sx={{ fontFamily: "Verdana", fontSize: "12px" }}>
                                  Institutional
                              </MenuItem>
                          </TextField>

                        <TextField select label="Status"
                            name="status"
                            value={form.status}
                            onChange={handleChange}
                            fullWidth size="small"
                            inputRef={statusRef}
                            sx={{
                                "& .MuiInputBase-input": {
                                    fontFamily: "Verdana",
                                    fontSize: "12px"
                                }
                            }}
                        >
                            <MenuItem value="Active" sx={{ fontFamily: "Verdana", fontSize: "12px" }}>
                                Active
                            </MenuItem>

                            <MenuItem value="Inactive" sx={{ fontFamily: "Verdana", fontSize: "12px" }}>
                                Inactive
                            </MenuItem>
                        </TextField>
                    </div>

                </div>

                <div style={styles.buttonRow}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        sx={{
                            height: "28px",
                            minHeight: "28px",
                            fontSize: "12px",
                            fontFamily: "Verdana",
                            padding: "0 12px",
                            textTransform: "none !important"
                        }}
                    >
                        {isEdit ? "Update" : "Save"}
                    </Button>

                    {isEdit && (
                        <Button
                            variant="contained"
                            onClick={resetForm}
                            sx={{
                                height: "28px",
                                minHeight: "28px",
                                fontSize: "12px",
                                fontFamily: "Verdana",
                                padding: "0 12px",
                                textTransform: "none !important",
                                marginLeft: "10px",
                                background: "#2f3542",   // 🔵 dark color (or use same as update if you want)
                                color: "#fff",
                                "&:hover": {
                                    background: "#1e272e"
                                }
                            }}
                        >
                            Cancel
                        </Button>
                    )}
                </div>
            </div>

            {/* GRID */}
            <div style={styles.card}>

                <div style={styles.gridTop}>

                    {/* SHOW INACTIVE */}
                    <label style={{ fontFamily: "Verdana", fontSize: "12px", display: "flex", alignItems: "center", gap: "5px" }}>
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                        />
                        Show Inactive
                    </label>

                    {/* SEARCH */}
                    <TextField
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        size="small"
                        sx={{
                            width: "220px",

                            "& .MuiOutlinedInput-root": {
                                height: "28px",
                                fontFamily: "Verdana"
                            },

                            "& .MuiOutlinedInput-input": {
                                padding: "4px 10px",
                                fontSize: "12px",
                                fontFamily: "Verdana"
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: search && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearch("")}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* EXPORT BUTTON */}
                    <Button
                        onClick={exportToExcel}
                        variant="contained"
                        sx={{
                            width: "80px",              // already aligned with search
                            height: "28px",              // ✅ match textbox height
                            minHeight: "28px",           // 🔥 override MUI default
                            fontFamily: "Verdana",
                            fontSize: "12px",
                            textTransform: "none",
                            padding: "0 10px",           // 🔥 control inner spacing
                            lineHeight: "1",             // 🔥 vertical alignment fix

                            backgroundColor: "#4f46e5",
                            color: "#fff",

                            "&:hover": {
                                backgroundColor: "#4338ca"
                            }
                        }}
                    >
                        EXPORT
                    </Button>

                    {/* ROWS DROPDOWN */}
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <span style={{ fontFamily: "Verdana", fontSize: "12px" }}>Rows</span>

                        <select
                            value={rowsPerPage}
                            onChange={(e) => setRowsPerPage(parseInt(e.target.value))}
                            style={{
                                fontFamily: "Verdana",
                                fontSize: "12px",
                                padding: "4px 6px"
                            }}
                        >
                            {[5, 10, 20, 50, 80, 120].map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>

                </div>

                <div style={{ width: "100%", overflowX: "auto" }}>

                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px", fontFamily: "Verdana", fontSize: "12px" }}>

                        <thead style={{ backgroundColor: "#5f6b7a", color: "#fff" }}>
                            <tr>

                                <th
                                    onClick={() => handleSort("custCode")}
                                    style={{ border: "1px solid #ccc", padding: "8px", cursor: "pointer" }}
                                >
                                    Code {sortField === "custCode" ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                                </th>

                                <th
                                    onClick={() => handleSort("organizationName")}
                                    style={{ border: "1px solid #ccc", padding: "8px", cursor: "pointer" }}
                                >
                                    Organization Name {sortField === "organizationName" ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                                </th>

                                <th
                                    onClick={() => handleSort("custName")}
                                    style={{ border: "1px solid #ccc", padding: "8px", cursor: "pointer" }}
                                >
                                    Name {sortField === "custName" ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                                </th>

                                <th
                                    onClick={() => handleSort("mobileNo")}
                                    style={{ border: "1px solid #ccc", padding: "8px", cursor: "pointer" }}
                                >
                                    Mobile {sortField === "mobileNo" ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                                </th>

                                <th
                                    onClick={() => handleSort("customerType")}
                                    style={{ border: "1px solid #ccc", padding: "8px", cursor: "pointer" }}
                                >
                                    Type {sortField === "customerType" ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                                </th>

                                <th
                                    onClick={() => handleSort("status")}
                                    style={{ border: "1px solid #ccc", padding: "8px", cursor: "pointer" }}
                                >
                                    Status {sortField === "status" ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                                </th>

                                <th style={{ border: "1px solid #ccc", padding: "8px" }}>
                                    Actions
                                </th>

                            </tr>
                        </thead>

                        <tbody>
                            {paginated.map(c => (
                                <tr key={c.id}>

                                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{c.custCode}</td>

                                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                        {c.organizationName}
                                    </td>

                                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{c.custName}</td>

                                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{c.mobileNo}</td>

                                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>{c.customerType}</td>

                                    <td style={{
                                        border: "1px solid #ddd",
                                        padding: "8px",
                                        color: c.status === "Active" ? "green" : "red",
                                        fontWeight: "bold"
                                    }}>
                                        {c.status}
                                    </td>

                                    {/* ✅ ACTIONS COLUMN (REPLACED) */}
                                    <td style={{ border: "1px solid #ddd", padding: "8px" }}>
                                        <ActionButtons
                                            row={c}
                                            status={c.status}
                                            onEdit={handleEdit}
                                            onDelete={handleDelete}
                                            onRestore={handleRestore}
                                            onPermanentDelete={handlePermanentDelete}
                                            canEdit={canEdit}
                                            canDelete={canDelete}
                                        />
                                    </td>

                                </tr>
                            ))}
                        </tbody>
                    </table>

                </div>

                {/* PAGINATION */}
                  <div style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: "10px",
                      marginTop: "10px"
                  }}>
                      <button
                          onClick={() => setPage(prev => Math.max(prev - 1, 0))}
                          disabled={page === 0}
                      >
                          Prev
                      </button>

                      <span style={{ fontSize: "12px", fontFamily: "Verdana" }}>
                          Page {page + 1} of {totalPages}
                      </span>

                      <button
                          onClick={() => setPage(prev => prev + 1)}
                          disabled={page + 1 >= totalPages}
                      >
                          Next
                      </button>
                  </div>

            </div>

              <Snackbar
                  open={snackbar.open}
                  autoHideDuration={3000}
                  onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
              >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>

        </div>
    );
}

const styles = {
    container: { padding: "5px", background: "#f1f2f6", fontFamily: "Verdana", fontSize: "12px" },
    header: { background: "#2f3542", color: "#fff", padding: "6px", borderRadius: "6px", marginBottom: "10px" },
    card: { background: "#fff", padding: "15px", borderRadius: "15px", marginBottom: "10px" },

    formContainer: { display: "flex", flexDirection: "column", gap: "10px" },
    row: { display: "flex", gap: "10px" },

    buttonRow: { marginTop: "10px" },

    gridTop: { display: "flex", justifyContent: "flex-end", gap: "10px", marginBottom: "10px" },

    table: { width: "100%", borderCollapse: "collapse" },
    tableHeader: { background: "#57606f", color: "#fff" },

    editBtn: { background: "#2ed573", border: "none", padding: "5px" },
    deleteBtn: { background: "#ff4757", border: "none", padding: "5px" },

    // 🔥 ADD THIS HERE
    restoreBtn: {
        backgroundColor: "#3b82f6",
        color: "#fff",
        border: "none",
        padding: "6px",
        marginLeft: "5px",
        cursor: "pointer",
        borderRadius: "4px"
    },

    pagination: { marginTop: "10px", display: "flex", justifyContent: "center", gap: "10px" },

    inputStyle: {
        "& .MuiOutlinedInput-root": {
            height: "34px"
        },

        "& .MuiInputBase-input": {
            fontFamily: "Verdana",
            fontSize: "12px",
            padding: "8px 10px"
        },

        "& .MuiInputLabel-root": {
            fontFamily: "Verdana",
            fontSize: "12px"
        },

        // 🔥 FIX DROPDOWN SPECIFIC
        "& .MuiSelect-select": {
            padding: "8px 10px !important",
            display: "flex",
            alignItems: "center"
        }
    }
};

export default Customer;